class ProductCardSlider {
  constructor(container) {
    this.container = container;
    this.track = container.querySelector('.product-card-slider__track');
    this.originalSlides = Array.from(container.querySelectorAll('.product-card-slider__slide'));
    this.prevBtn = container.querySelector('.product-card-swiper__prev');
    this.nextBtn = container.querySelector('.product-card-swiper__next');
    this.totalSlides = this.originalSlides.length;
    this.slideWidth = 0;
    this.currentPosition = 0;
    this.animationId = null;
    this.animationStart = 0;
    this.animationFrom = 0;
    this.animationTo = 0;
    this.duration = 500;

    // Drag state
    this.isDragging = false;
    this.dragDidMove = false;
    this.dragPointerId = null;
    this.dragStartX = 0;
    this.dragStartPos = 0;
    this.dragDelta = 0;
    this.dragStartTime = 0;
    this.threshold = 40;

    if (this.totalSlides > 1) {
      this.setupLoop();
      this.bindEvents();
    }
  }

  setupLoop() {
    var firstClone = this.originalSlides[0].cloneNode(true);
    var lastClone = this.originalSlides[this.totalSlides - 1].cloneNode(true);

    firstClone.setAttribute('data-clone', 'true');
    lastClone.setAttribute('data-clone', 'true');

    this.track.appendChild(firstClone);
    this.track.insertBefore(lastClone, this.track.firstChild);

    this.currentIndex = 1;
    this.slideWidth = this.container.offsetWidth;
    this.setPosition(-(this.currentIndex * this.slideWidth));
  }

  updateSlideWidth() {
    this.slideWidth = this.container.offsetWidth;
  }

  bindEvents() {
    this.nextBtn && this.nextBtn.addEventListener('click', this.next.bind(this));
    this.prevBtn && this.prevBtn.addEventListener('click', this.prev.bind(this));
    this.boundOnResize = this.onResize.bind(this);
    window.addEventListener('resize', this.boundOnResize);

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.boundOnResize);
      this.resizeObserver.observe(this.container);
    }

    // Drag events
    this.track.addEventListener('pointerdown', this.onDragStart.bind(this));
    this.track.addEventListener('pointermove', this.onDragMove.bind(this));
    this.track.addEventListener('pointerup', this.onDragEnd.bind(this));
    this.track.addEventListener('pointercancel', this.onDragEnd.bind(this));
    this.track.addEventListener('dragstart', function (e) { e.preventDefault(); });
    this.track.addEventListener('click', this.onClickAfterDrag.bind(this), true);
  }

  // --- Drag handlers ---

  onDragStart(e) {
    if (e.button && e.button !== 0) return;

    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    this.isDragging = true;
    this.dragDidMove = false;
    this.dragPointerId = e.pointerId;
    this.dragStartX = e.clientX;
    this.dragStartPos = this.currentPosition;
    this.dragDelta = 0;
    this.dragStartTime = Date.now();
  }

  onDragMove(e) {
    if (!this.isDragging) return;

    this.dragDelta = e.clientX - this.dragStartX;

    if (!this.dragDidMove && Math.abs(this.dragDelta) > this.threshold) {
      this.dragDidMove = true;
      this.track.setPointerCapture(this.dragPointerId);
      this.track.style.cursor = 'grabbing';
    }

    if (this.dragDidMove) {
      var newPos = this.dragStartPos + this.dragDelta;
      this.setPosition(newPos);
    }
  }

  onDragEnd() {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.track.style.cursor = '';

    if (!this.dragDidMove) return;

    var elapsed = Date.now() - this.dragStartTime;
    var velocity = this.dragDelta / elapsed;
    var absDelta = Math.abs(this.dragDelta);
    var isSwipe = absDelta > this.threshold || Math.abs(velocity) > 0.3;

    if (isSwipe && this.dragDelta < 0) {
      this.handleLoopJump();
      this.currentIndex++;
    } else if (isSwipe && this.dragDelta > 0) {
      this.handleLoopJump();
      this.currentIndex--;
    }

    this.animateTo(-(this.currentIndex * this.slideWidth));
  }

  onClickAfterDrag(e) {
    if (this.dragDidMove) {
      e.preventDefault();
      e.stopPropagation();
      this.dragDidMove = false;
    }
  }

  // --- Resize ---

  onResize() {
    var newWidth = this.container.offsetWidth;
    if (newWidth === this.slideWidth) return;

    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    this.slideWidth = newWidth;
    this.setPosition(-(this.currentIndex * this.slideWidth));
  }

  // --- Arrow navigation ---

  next() {
    this.handleLoopJump();
    this.currentIndex++;
    this.animateTo(-(this.currentIndex * this.slideWidth));
  }

  prev() {
    this.handleLoopJump();
    this.currentIndex--;
    this.animateTo(-(this.currentIndex * this.slideWidth));
  }

  handleLoopJump() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
      this.setPosition(this.animationTo);
    }

    if (this.currentIndex === 0) {
      this.currentIndex = this.totalSlides;
      this.setPosition(-(this.currentIndex * this.slideWidth));
    } else if (this.currentIndex > this.totalSlides) {
      this.currentIndex = 1;
      this.setPosition(-(this.currentIndex * this.slideWidth));
    }
  }

  // --- Animation ---

  easeCubicBezier(t) {
    var cx = 3 * 0.35;
    var bx = 3 * (0.15 - 0.35) - cx;
    var ax = 1 - cx - bx;

    var cy = 3 * 0;
    var by = 3 * (1 - 0) - cy;
    var ay = 1 - cy - by;

    var s = t;
    for (var i = 0; i < 8; i++) {
      var xS = ((ax * s + bx) * s + cx) * s;
      var dxS = (3 * ax * s + 2 * bx) * s + cx;
      if (Math.abs(dxS) < 1e-6) break;
      s = s - (xS - t) / dxS;
    }

    s = Math.max(0, Math.min(1, s));
    return ((ay * s + by) * s + cy) * s;
  }

  animateTo(target) {
    var self = this;

    this.animationFrom = this.currentPosition;
    this.animationTo = target;
    this.animationStart = performance.now();

    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }

    function tick(now) {
      var elapsed = now - self.animationStart;
      var progress = Math.min(elapsed / self.duration, 1);
      var eased = self.easeCubicBezier(progress);
      var pos = self.animationFrom + (self.animationTo - self.animationFrom) * eased;

      self.setPosition(pos);

      if (progress < 1) {
        self.animationId = requestAnimationFrame(tick);
      } else {
        self.animationId = null;
        self.onAnimationComplete();
      }
    }

    this.animationId = requestAnimationFrame(tick);
  }

  setPosition(pos) {
    this.currentPosition = pos;
    this.track.style.transform = 'translate3d(' + pos + 'px, 0px, 0px)';
  }

  onAnimationComplete() {
    if (this.currentIndex === 0) {
      this.currentIndex = this.totalSlides;
      this.setPosition(-(this.currentIndex * this.slideWidth));
    } else if (this.currentIndex === this.totalSlides + 1) {
      this.currentIndex = 1;
      this.setPosition(-(this.currentIndex * this.slideWidth));
    }
  }

  // --- Destroy (for reinit) ---

  destroy() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.boundOnResize) {
      window.removeEventListener('resize', this.boundOnResize);
    }
    this.track.style.transform = '';
    var clones = this.track.querySelectorAll('[data-clone]');
    clones.forEach(function (clone) { clone.remove(); });
    this.originalSlides.forEach(function (slide) { slide.style.width = ''; });
    delete this.container.dataset.sliderInit;
  }
}

window.initProductCardSlider = function (selector) {
  selector = selector || '.product-card-slider';

  document.querySelectorAll(selector).forEach(function (el) {
    if (el.dataset.sliderInit) return;
    el.dataset.sliderInit = 'true';

    new ProductCardSlider(el);
  });
};

document.addEventListener('DOMContentLoaded', function () {
  window.initProductCardSlider();
});

document.addEventListener('shopify:section:load', function () {
  window.initProductCardSlider();
});
